<template>
  <div v-if="hasMessagesInUnread" class="tw-flex tw-flex-col tw-pb-4 tw-px-4">
    <div
      class="tw-h-4 tw-bg-sf-snow-lighter tw-w-full tw-top-0 tw-sticky tw-z-50"
    />
    <div class="tw-flex tw-flex-col tw-gap-4">
      <RemarkGroup
        v-if="!!groupedUnread[COMPANY_CATEGORY]"
        :name="I18n.t('company.start')"
        :show-mark-as-read="clientOverviewHasUnread"
        :disable-mark-as-read="markingAsRead"
        @mark-as-read="markCategoryAsRead(COMPANY_CATEGORY)"
      >
        <template #default>
          <template
            v-for="comm in groupedUnread[COMPANY_CATEGORY][
              CLIENT_OVERVIEW_SECTION
            ]"
            :key="`${comm.kind}-${comm.id}`"
          >
            <RemarkCard
              v-if="comm.kind === 'remark-card'"
              :remark="comm"
              show-context-btn
              show-tags
              part-of="inbox"
            />
            <NotificationContent
              v-else
              type="CheckList"
              :communication-item="comm"
            />
          </template>
        </template>
        <template #message-count-indicator>
          {{
            I18n.t('communication.inbox.messages', {
              count:
                groupedUnread[COMPANY_CATEGORY][CLIENT_OVERVIEW_SECTION].length,
            })
          }}
        </template>
      </RemarkGroup>
      <InboxTalkingPointsCard
        v-if="!!groupedUnread[CLIENT_MEETING_CATEGORY]"
        :buckets="groupedUnread[CLIENT_MEETING_CATEGORY]"
      />
      <RemarkGroup
        v-for="section in unreadSections"
        :key="section.categoryId"
        :name="section.categoryName"
        :show-mark-as-read="section.hasUnread"
        :disable-mark-as-read="markingAsRead"
        @mark-as-read="markCategoryAsRead(section.categoryId)"
      >
        <template #default>
          <template
            v-for="item in unreadSectionItems[section.categoryId]"
            :key="`${section.categoryName}-${item}`"
          >
            <template
              v-for="comm in groupedUnread[section.categoryId][item]"
              :key="`${comm.kind}-${comm.id}`"
            >
              <RemarkCard
                v-if="comm.kind === 'remark-card'"
                :remark="comm"
                show-context-btn
                show-context
                show-tags
                part-of="inbox"
              />
              <NotificationContent
                v-else
                type="CheckList"
                :communication-item="comm"
              />
            </template>
          </template>
        </template>
        <template #message-count-indicator>
          {{
            I18n.t('communication.inbox.messages', {
              count: section.unreadCount,
            })
          }}
        </template>
      </RemarkGroup>
      <div
        class="
          tw-mx-auto
          tw-my-4
          tw-font-medium
          tw-text-sm
          tw-text-sf-gray-light
          tw-text-center
        "
      >
        <SfButtonDs
          size="sm"
          icon-before="envelope-open"
          data-testid="inbox-mark-all-as-read"
          :disabled="markingAsRead"
          @click="markAllAsRead"
        >
          {{ I18n.t('communication.inbox.mark_all_as_read') }}
        </SfButtonDs>
      </div>
    </div>
  </div>
  <div
    v-else
    class="
      tw-h-full tw-w-full tw-flex tw-flex-col tw-items-center tw-justify-center
    "
  >
    <span class="tw-font-medium tw-text-2xl">
      {{ I18n.t('communication.inbox.no_unread') }}
    </span>
  </div>
</template>

<script setup>
import RemarkGroup from '@app/application/communication/screens/inbox/components/remark-group.vue'
import RemarkCard from '@app/shared/vue/components/molecules/remark/remark-card.vue'
import NotificationContent from '@app/application/communication/screens/inbox/components/notification-content.vue'
import InboxTalkingPointsCard from '@app/application/communication/screens/inbox/components/inbox-talking-points-card.vue'
import SfButtonDs from '../../../../../shared/vue/components/sf-button/sf-button-ds.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { useStore } from 'vuex'
import { computed } from 'vue'
import {
  CLIENT_MEETING_CATEGORY,
  CLIENT_OVERVIEW_SECTION,
  COMPANY_CATEGORY,
} from '@app/application/communication/screens/inbox/store/utils'
import useGroupedFilteredCommunications from '@app/application/communication/screens/inbox/composables/use-grouped-filtered-communications'

const I18n = useI18n()
const store = useStore()

const {
  groupedUnread,
  markCategoryAsRead,
  markAllAsRead,
  menuSections,
  clientOverviewHasUnread,
} = useGroupedFilteredCommunications()

const hasMessagesInUnread = computed(
  () => Object.keys(groupedUnread.value).length > 0
)

const markingAsRead = computed(() => store.state.sections.markingAsRead)
const unreadSections = computed(() => {
  const result = []
  for (const section of menuSections.value) {
    if (groupedUnread.value[section.categoryId]) {
      const unreadCount = Object.values(
        groupedUnread.value[section.categoryId]
      ).reduce((acc, item) => acc + item.length, 0)
      result.push({
        ...section,
        unreadCount,
      })
    }
  }
  return result
})
const unreadSectionItems = computed(() => {
  const result = {}
  for (const section of unreadSections.value) {
    result[section.categoryId] = Object.keys(
      groupedUnread.value[section.categoryId]
    )
  }
  return result
})
</script>

<style scoped>
.remark-group {
  --sf-remark-group-sticky-offset: 16px;
}
</style>
